import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  Button,
  Tab,
  Tabs,
  LinearProgress,
  CircularProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Search,
  Delete,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Cancel,
  PlayArrow,
  Quiz as QuizIcon,
  Translate,
  Subtitles,
  Refresh,
  VideoLibrary,
  OpenInNew,
  ExpandMore,
  Edit,
  Save,
  Add,
  Spellcheck,
  ContentPaste,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';

import { supabase } from '../lib/supabase';
import { VideoContent, QuizContent } from '../types';
import { defaultDataGridStyles } from '../utils';

// ============================================================================
// TYPES
// ============================================================================

interface VideoRow extends VideoContent {
  creatorUsername?: string;
  creatorEmail?: string;
}

interface QuizRow extends QuizContent {
  videoTitle?: string;
}

interface GenerationStats {
  total: number;
  withQuiz: number;
  withVocabulary: number;
  withSubtitles: number;
}

const GRAMMAR_SKILLS = [
  { value: 'prepositions', label: 'Prepositions' },
  { value: 'verb_tenses', label: 'Verb Tenses' },
  { value: 'articles', label: 'Articles' },
  { value: 'countable_uncountable', label: 'Countable / Uncountable' },
  { value: 'subject_verb_agreement', label: 'Subject-Verb Agreement' },
  { value: 'comparatives_superlatives', label: 'Comparatives / Superlatives' },
  { value: 'connectors', label: 'Connectors' },
  { value: 'modals', label: 'Modals' },
  { value: 'relative_clauses', label: 'Relative Clauses' },
];

interface GrammarFormState {
  grammar_skill: string;
  source_sentence: string;
  sentence_with_blanks: string;
  correct_answers: string[];
  word_bank: string;
  explanation_en: string;
  explanation_zh: string;
}

const EMPTY_GRAMMAR_FORM: GrammarFormState = {
  grammar_skill: '',
  source_sentence: '',
  sentence_with_blanks: '',
  correct_answers: [],
  word_bank: '',
  explanation_en: '',
  explanation_zh: '',
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
      {value === index && children}
    </Box>
  );
}

// ============================================================================
// CONTENT MANAGEMENT PAGE
// ============================================================================

const ContentManagementPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Videos state
  const [videos, setVideos] = useState<VideoRow[]>([]);
  const [videosLoading, setVideosLoading] = useState(true);
  const [totalVideos, setTotalVideos] = useState(0);
  const [videosPage, setVideosPage] = useState(0);
  const [videosPageSize, setVideosPageSize] = useState(25);
  const [videoSearch, setVideoSearch] = useState('');
  const [sourceTypeFilter, setSourceTypeFilter] = useState<string>('');
  const [publishedFilter, setPublishedFilter] = useState<string>('');
  const [generationFilter, setGenerationFilter] = useState<string>('');

  // Quizzes state
  const [quizzes, setQuizzes] = useState<QuizRow[]>([]);
  const [quizzesLoading, setQuizzesLoading] = useState(true);
  const [totalQuizzes, setTotalQuizzes] = useState(0);
  const [quizzesPage, setQuizzesPage] = useState(0);
  const [quizzesPageSize, setQuizzesPageSize] = useState(25);

  // Generation stats
  const [stats, setStats] = useState<GenerationStats>({
    total: 0,
    withQuiz: 0,
    withVocabulary: 0,
    withSubtitles: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);

  // Dialog state
  const [videoDetailDialog, setVideoDetailDialog] = useState<{
    open: boolean;
    video: VideoRow | null;
  }>({ open: false, video: null });

  const [quizDetailDialog, setQuizDetailDialog] = useState<{
    open: boolean;
    quiz: QuizRow | null;
  }>({ open: false, quiz: null });

  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    open: boolean;
    video: VideoRow | null;
  }>({ open: false, video: null });

  const [regenerating, setRegenerating] = useState(false);

  // Video detail quiz/vocab data
  const [videoQuiz, setVideoQuiz] = useState<any>(null);
  const [videoVocab, setVideoVocab] = useState<any[]>([]);
  const [loadingVideoDetails, setLoadingVideoDetails] = useState(false);

  // Quiz editing state
  const [editingQuizId, setEditingQuizId] = useState<string | null>(null);
  const [editingQuizJson, setEditingQuizJson] = useState<string>('');
  const [savingQuiz, setSavingQuiz] = useState(false);

  // Vocabulary editing state
  const [editingVocabId, setEditingVocabId] = useState<string | null>(null);
  const [editingVocab, setEditingVocab] = useState<any>({});
  const [savingVocab, setSavingVocab] = useState(false);

  // Transcript state
  const [videoSubtitles, setVideoSubtitles] = useState<any[]>([]);
  const [editingSubId, setEditingSubId] = useState<string | null>(null);
  const [editingSub, setEditingSub] = useState<{ text_en: string; text_zh: string }>({ text_en: '', text_zh: '' });
  const [savingSub, setSavingSub] = useState(false);

  // Grammar question state
  const [grammarDialogOpen, setGrammarDialogOpen] = useState(false);
  const [grammarTargetVideoId, setGrammarTargetVideoId] = useState<string>('');
  const [grammarMode, setGrammarMode] = useState<'form' | 'json'>('form');
  const [grammarForm, setGrammarForm] = useState<GrammarFormState>(EMPTY_GRAMMAR_FORM);
  const [grammarJson, setGrammarJson] = useState<string>('');
  const [grammarErrors, setGrammarErrors] = useState<string[]>([]);
  const [savingGrammar, setSavingGrammar] = useState(false);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const db = supabase as any;

      const [totalRes, quizRes, vocabRes, subsRes] = await Promise.all([
        db.from('videos').select('id', { count: 'exact', head: true }),
        db.from('videos').select('id', { count: 'exact', head: true }).eq('quiz_generated', true),
        db.from('videos').select('id', { count: 'exact', head: true }).eq('vocabulary_generated', true),
        db.from('videos').select('id', { count: 'exact', head: true }).eq('subtitles_generated', true),
      ]);

      setStats({
        total: totalRes.count ?? 0,
        withQuiz: quizRes.count ?? 0,
        withVocabulary: vocabRes.count ?? 0,
        withSubtitles: subsRes.count ?? 0,
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchVideos = useCallback(async () => {
    try {
      setVideosLoading(true);
      const db = supabase as any;

      let query = db
        .from('videos')
        .select('*, profiles!creator_id(username, email)', { count: 'exact' })
        .order('created_at', { ascending: false });

      if (videoSearch.trim()) {
        query = query.ilike('title', `%${videoSearch.trim()}%`);
      }
      if (sourceTypeFilter) {
        query = query.eq('source_type', sourceTypeFilter);
      }
      if (publishedFilter === 'yes') {
        query = query.eq('is_published', true);
      } else if (publishedFilter === 'no') {
        query = query.eq('is_published', false);
      }
      if (generationFilter === 'missing_quiz') {
        query = query.eq('quiz_generated', false);
      } else if (generationFilter === 'missing_vocabulary') {
        query = query.eq('vocabulary_generated', false);
      }

      const from = videosPage * videosPageSize;
      const to = from + videosPageSize - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      const mapped: VideoRow[] = (data || []).map((row: any) => ({
        id: row.id,
        title: row.title || '',
        description: row.description || '',
        creatorId: row.creator_id || '',
        creatorName: row.profiles?.username || row.creator_id?.slice(0, 8) || 'Unknown',
        creatorUsername: row.profiles?.username,
        creatorEmail: row.profiles?.email,
        videoUrl: row.video_url || '',
        thumbnailUrl: row.thumbnail_url || '',
        durationSeconds: row.duration_seconds || 0,
        subject: row.subject || '',
        isPublished: row.is_published ?? false,
        viewCount: row.view_count || 0,
        likeCount: row.like_count || 0,
        commentCount: row.comment_count || 0,
        youtubeVideoId: row.youtube_video_id,
        sourceType: row.source_type || 'local',
        hasCaptions: row.has_captions ?? false,
        youtubePlaylistId: row.youtube_playlist_id,
        quizGenerated: row.quiz_generated ?? false,
        vocabularyGenerated: row.vocabulary_generated ?? false,
        subtitlesGenerated: row.subtitles_generated ?? false,
        createdAt: row.created_at,
      }));

      setVideos(mapped);
      setTotalVideos(count ?? 0);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to fetch videos', { variant: 'error' });
    } finally {
      setVideosLoading(false);
    }
  }, [videosPage, videosPageSize, videoSearch, sourceTypeFilter, publishedFilter, generationFilter, enqueueSnackbar]);

  const fetchQuizzes = useCallback(async () => {
    try {
      setQuizzesLoading(true);
      const db = supabase as any;

      const from = quizzesPage * quizzesPageSize;
      const to = from + quizzesPageSize - 1;

      const { data, error, count } = await db
        .from('quizzes')
        .select('*, videos!video_id(title)', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      const mapped: QuizRow[] = (data || []).map((row: any) => ({
        id: row.id,
        videoId: row.video_id || '',
        title: row.title || '',
        questionsEn: row.questions_en,
        questionsZh: row.questions_zh,
        difficultyLevel: row.difficulty_level || '',
        language: row.language || '',
        category: row.category || '',
        quizFocus: row.quiz_focus || '',
        createdAt: row.created_at,
        videoTitle: row.videos?.title || 'Unknown Video',
      }));

      setQuizzes(mapped);
      setTotalQuizzes(count ?? 0);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to fetch quizzes', { variant: 'error' });
    } finally {
      setQuizzesLoading(false);
    }
  }, [quizzesPage, quizzesPageSize, enqueueSnackbar]);

  const fetchVideoQuizAndVocab = useCallback(async (videoId: string) => {
    setLoadingVideoDetails(true);
    setVideoQuiz(null);
    setVideoVocab([]);
    setVideoSubtitles([]);

    try {
      const db = supabase as any;
      // Get the video's youtube_video_id for subtitle lookup
      const { data: videoRow } = await db.from('videos').select('youtube_video_id').eq('id', videoId).single();
      const ytVideoId = videoRow?.youtube_video_id;

      const [quizRes, vocabRes, subtitleRes] = await Promise.all([
        db.from('quizzes').select('*').eq('video_id', videoId).order('created_at', { ascending: false }),
        db.from('vocabulary_words').select('*').eq('video_id', videoId).order('created_at', { ascending: true }),
        ytVideoId
          ? db.from('video_subtitle_segments').select('*').eq('youtube_video_id', ytVideoId).order('start_time', { ascending: true })
          : db.from('video_subtitle_segments').select('*').eq('video_id', videoId).order('start_time', { ascending: true }),
      ]);

      if (quizRes.data && quizRes.data.length > 0) setVideoQuiz(quizRes.data);
      if (vocabRes.data) setVideoVocab(vocabRes.data);
      if (subtitleRes.data) setVideoSubtitles(subtitleRes.data);
    } catch (error) {
      console.error('Failed to fetch quiz/vocab/subtitles:', error);
    } finally {
      setLoadingVideoDetails(false);
    }
  }, []);

  useEffect(() => {
    if (videoDetailDialog.open && videoDetailDialog.video) {
      fetchVideoQuizAndVocab(videoDetailDialog.video.id);
      setEditingQuizId(null);
    }
  }, [videoDetailDialog.open, videoDetailDialog.video, fetchVideoQuizAndVocab]);

  const handleSaveQuiz = async (quizId: string) => {
    try {
      const parsed = JSON.parse(editingQuizJson);
      setSavingQuiz(true);
      const db = supabase as any;
      const { data, error } = await db
        .from('quizzes')
        .update({ questions: parsed, question_count: Array.isArray(parsed) ? parsed.length : 0, updated_at: new Date().toISOString() })
        .eq('quiz_id', quizId)
        .select();

      if (error) throw error;

      if (!data || data.length === 0) {
        enqueueSnackbar('Update did not match any rows. Check RLS policies on the quizzes table.', { variant: 'warning' });
      } else {
        // Update local state immediately
        setVideoQuiz((prev: any[]) =>
          prev.map((q: any) => q.quiz_id === quizId ? { ...q, questions: parsed, question_count: Array.isArray(parsed) ? parsed.length : 0 } : q)
        );
        enqueueSnackbar('Quiz questions updated successfully', { variant: 'success' });
      }
      setEditingQuizId(null);
    } catch (err: any) {
      enqueueSnackbar(err instanceof SyntaxError
        ? 'Invalid JSON format. Please check your edits.'
        : err.message || 'Failed to save quiz',
        { variant: 'error' }
      );
    } finally {
      setSavingQuiz(false);
    }
  };

  const handleSaveVocab = async (vocabId: string) => {
    try {
      setSavingVocab(true);
      const db = supabase as any;
      const { data, error } = await db
        .from('vocabulary_words')
        .update({
          word: editingVocab.word,
          definition_en: editingVocab.definition_en,
          definition_zh: editingVocab.definition_zh,
          part_of_speech: editingVocab.part_of_speech,
          context_sentence: editingVocab.context_sentence,
          context_translation: editingVocab.context_translation,
          updated_at: new Date().toISOString(),
        })
        .eq('id', vocabId)
        .select();

      if (error) throw error;

      if (!data || data.length === 0) {
        enqueueSnackbar('Update failed. Check RLS policies on vocabulary_words.', { variant: 'warning' });
      } else {
        setVideoVocab((prev: any[]) => prev.map((v: any) => v.id === vocabId ? { ...v, ...editingVocab } : v));
        enqueueSnackbar('Vocabulary word updated', { variant: 'success' });
      }
      setEditingVocabId(null);
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to save vocabulary', { variant: 'error' });
    } finally {
      setSavingVocab(false);
    }
  };

  const handleSaveSubtitle = async (subId: string) => {
    try {
      setSavingSub(true);
      const db = supabase as any;
      const { data, error } = await db
        .from('video_subtitle_segments')
        .update({ text_en: editingSub.text_en, text_zh: editingSub.text_zh, updated_at: new Date().toISOString() })
        .eq('id', subId)
        .select();

      if (error) throw error;

      if (!data || data.length === 0) {
        enqueueSnackbar('Update failed. Check RLS policies on video_subtitle_segments.', { variant: 'warning' });
      } else {
        setVideoSubtitles((prev: any[]) => prev.map((s: any) => s.id === subId ? { ...s, ...editingSub } : s));
        enqueueSnackbar('Transcript updated', { variant: 'success' });
      }
      setEditingSubId(null);
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to save transcript', { variant: 'error' });
    } finally {
      setSavingSub(false);
    }
  };

  // Grammar question helpers
  const blankCount = (grammarForm.sentence_with_blanks.match(/___/g) || []).length;

  const openGrammarDialog = (videoId: string) => {
    setGrammarTargetVideoId(videoId);
    setGrammarForm(EMPTY_GRAMMAR_FORM);
    setGrammarJson('');
    setGrammarErrors([]);
    setGrammarMode('form');
    setGrammarDialogOpen(true);
  };

  const validateGrammarQuestion = (q: any): string[] => {
    const errors: string[] = [];
    if (!q.grammar_skill) errors.push('Grammar skill is required');
    if (!q.source_sentence || q.source_sentence.trim().split(/\s+/).length < 8) errors.push('Source sentence must have at least 8 words');
    if (!q.sentence_with_blanks || !(q.sentence_with_blanks.includes('___'))) errors.push('Sentence with blanks must contain at least one ___');
    const blanksCount = (q.sentence_with_blanks?.match(/___/g) || []).length;
    if (!q.blanks || q.blanks.length !== blanksCount) errors.push(`Number of correct answers (${q.blanks?.length || 0}) must match number of blanks (${blanksCount})`);
    if (q.blanks) {
      for (let i = 0; i < q.blanks.length; i++) {
        if (!q.blanks[i]?.correct_answer?.trim()) errors.push(`Correct answer for blank ${i + 1} is empty`);
      }
    }
    const wordBankItems = Array.isArray(q.word_bank) ? q.word_bank : (q.word_bank || '').split(',').map((w: string) => w.trim()).filter(Boolean);
    if (wordBankItems.length < blanksCount + 2) errors.push(`Word bank must have at least ${blanksCount + 2} items (correct answers + distractors)`);
    if (q.blanks) {
      for (const b of q.blanks) {
        if (b.correct_answer && !wordBankItems.includes(b.correct_answer.trim())) {
          errors.push(`Word bank must contain the correct answer "${b.correct_answer}"`);
        }
      }
    }
    if (!q.explanation_en?.trim()) errors.push('English explanation is required');
    if (!q.explanation_zh?.trim()) errors.push('Chinese explanation is required');
    if (q.type && q.type !== 'transcript_cloze_bubble') errors.push('Type must be "transcript_cloze_bubble"');
    return errors;
  };

  const buildGrammarQuestionFromForm = (): any => {
    const wordBankItems = grammarForm.word_bank.split(',').map(w => w.trim()).filter(Boolean);
    const blanks = grammarForm.correct_answers.map((answer, idx) => ({
      position: idx,
      correct_answer: answer.trim(),
    }));
    return {
      id: '', // auto-generated on save
      type: 'transcript_cloze_bubble',
      grammar_skill: grammarForm.grammar_skill,
      source_sentence: grammarForm.source_sentence.trim(),
      sentence_with_blanks: grammarForm.sentence_with_blanks.trim(),
      blanks,
      word_bank: wordBankItems,
      explanation: grammarForm.explanation_zh.trim(),
      explanation_en: grammarForm.explanation_en.trim(),
      explanation_zh: grammarForm.explanation_zh.trim(),
    };
  };

  const handleSaveGrammarQuestion = async () => {
    let questions: any[] = [];

    if (grammarMode === 'form') {
      const q = buildGrammarQuestionFromForm();
      const errors = validateGrammarQuestion(q);
      if (errors.length > 0) { setGrammarErrors(errors); return; }
      questions = [q];
    } else {
      try {
        const parsed = JSON.parse(grammarJson);
        const arr = Array.isArray(parsed) ? parsed : [parsed];
        const allErrors: string[] = [];
        for (let i = 0; i < arr.length; i++) {
          const errs = validateGrammarQuestion(arr[i]);
          if (errs.length > 0) allErrors.push(...errs.map(e => `Question ${i + 1}: ${e}`));
          if (!arr[i].type) arr[i].type = 'transcript_cloze_bubble';
          if (!arr[i].explanation && arr[i].explanation_zh) arr[i].explanation = arr[i].explanation_zh;
        }
        if (allErrors.length > 0) { setGrammarErrors(allErrors); return; }
        questions = arr;
      } catch {
        setGrammarErrors(['Invalid JSON. Please check your input.']);
        return;
      }
    }

    setGrammarErrors([]);
    setSavingGrammar(true);

    try {
      const db = supabase as any;
      const { data: quizRows, error: fetchErr } = await db
        .from('quizzes')
        .select('quiz_id, questions, language')
        .eq('video_id', grammarTargetVideoId)
        .in('language', ['en', 'zh-TW']);

      if (fetchErr) throw fetchErr;
      if (!quizRows || quizRows.length === 0) {
        setGrammarErrors(['No EN or ZH-TW quiz found for this video. Generate quizzes first.']);
        setSavingGrammar(false);
        return;
      }

      for (const row of quizRows) {
        const existing = Array.isArray(row.questions) ? [...row.questions] : [];
        const existingGrammarCount = existing.filter((q: any) => q.type === 'transcript_cloze_bubble').length;

        for (let i = 0; i < questions.length; i++) {
          const q = { ...questions[i] };
          q.id = `grammar_${existingGrammarCount + i + 1}`;
          q.explanation = row.language === 'en' ? q.explanation_en : q.explanation_zh;
          existing.push(q);
        }

        const { error: updateErr } = await db
          .from('quizzes')
          .update({ questions: existing, question_count: existing.length, updated_at: new Date().toISOString() })
          .eq('quiz_id', row.quiz_id)
          .select();

        if (updateErr) throw updateErr;
      }

      enqueueSnackbar(`Added ${questions.length} grammar question(s) to both EN and ZH-TW quizzes`, { variant: 'success' });
      setGrammarDialogOpen(false);
      if (videoDetailDialog.video) fetchVideoQuizAndVocab(videoDetailDialog.video.id);
    } catch (err: any) {
      setGrammarErrors([err.message || 'Failed to save grammar question']);
    } finally {
      setSavingGrammar(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  useEffect(() => {
    if (activeTab === 1) {
      fetchQuizzes();
    }
  }, [activeTab, fetchQuizzes]);

  // ============================================================================
  // ACTIONS
  // ============================================================================

  const handleTogglePublish = async (video: VideoRow) => {
    try {
      const db = supabase as any;
      const { error } = await db
        .from('videos')
        .update({ is_published: !video.isPublished })
        .eq('id', video.id);

      if (error) throw error;

      enqueueSnackbar(
        video.isPublished ? 'Video unpublished' : 'Video published',
        { variant: 'success' },
      );
      fetchVideos();
      fetchStats();
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to update publish status', { variant: 'error' });
    }
  };

  const handleDeleteVideo = async () => {
    const video = deleteConfirmDialog.video;
    if (!video) return;

    try {
      const db = supabase as any;
      const { error } = await db.from('videos').delete().eq('id', video.id);
      if (error) throw error;

      enqueueSnackbar('Video deleted successfully', { variant: 'success' });
      setDeleteConfirmDialog({ open: false, video: null });
      fetchVideos();
      fetchStats();
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to delete video', { variant: 'error' });
    }
  };

  const handleRegenerateQuiz = async (video: VideoRow) => {
    if (!video.youtubeVideoId) {
      enqueueSnackbar('No YouTube video ID available for quiz generation', { variant: 'warning' });
      return;
    }

    try {
      setRegenerating(true);
      const backendUrl = process.env.REACT_APP_BACKEND_API_URL;
      const response = await fetch(`${backendUrl}/api/youtube/generate-quiz`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          youtube_video_id: video.youtubeVideoId,
          video_title: video.title,
          category: video.subject || 'general',
          language: 'en',
          generate_both_languages: true,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `Request failed with status ${response.status}`);
      }

      enqueueSnackbar('Quiz generation started successfully', { variant: 'success' });
      fetchVideos();
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to regenerate quiz', { variant: 'error' });
    } finally {
      setRegenerating(false);
    }
  };

  // ============================================================================
  // HELPER
  // ============================================================================

  const formatDuration = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const pct = (part: number, total: number) => (total > 0 ? Math.round((part / total) * 100) : 0);

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  const videoColumns: GridColDef[] = [
    {
      field: 'thumbnailUrl',
      headerName: 'Thumb',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Avatar
          variant="rounded"
          src={params.value}
          sx={{ width: 56, height: 40 }}
        >
          <PlayArrow fontSize="small" />
        </Avatar>
      ),
    },
    {
      field: 'title',
      headerName: 'Title',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Tooltip title={params.value || ''}>
          <Typography variant="body2" fontWeight="medium" noWrap>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'creatorName',
      headerName: 'Creator',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value || 'Unknown'}
        </Typography>
      ),
    },
    {
      field: 'sourceType',
      headerName: 'Source',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value === 'youtube_shorts' ? 'YouTube' : 'Local'}
          size="small"
          color={params.value === 'youtube_shorts' ? 'error' : 'info'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'isPublished',
      headerName: 'Published',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Yes' : 'No'}
          size="small"
          color={params.value ? 'success' : 'default'}
          variant={params.value ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'viewCount',
      headerName: 'Views',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2">
          {(params.value || 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'likeCount',
      headerName: 'Likes',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2">
          {(params.value || 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'quizGenerated',
      headerName: 'Quiz',
      width: 70,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) =>
        params.value ? (
          <CheckCircle fontSize="small" color="success" />
        ) : (
          <Cancel fontSize="small" color="disabled" />
        ),
    },
    {
      field: 'vocabularyGenerated',
      headerName: 'Vocab',
      width: 70,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) =>
        params.value ? (
          <CheckCircle fontSize="small" color="success" />
        ) : (
          <Cancel fontSize="small" color="disabled" />
        ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value ? format(new Date(params.value), 'MMM dd, yyyy') : '-'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" gap={0.5}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setVideoDetailDialog({ open: true, video: params.row });
              }}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={params.row.isPublished ? 'Unpublish' : 'Publish'}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleTogglePublish(params.row);
              }}
            >
              {params.row.isPublished ? (
                <VisibilityOff fontSize="small" />
              ) : (
                <Visibility fontSize="small" color="success" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteConfirmDialog({ open: true, video: params.row });
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const quizColumns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Title',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Tooltip title={params.value || ''}>
          <Typography variant="body2" fontWeight="medium" noWrap>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'videoTitle',
      headerName: 'Video',
      flex: 1.2,
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant="body2" noWrap color="text.secondary">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 140,
      renderCell: (params) => (
        <Chip label={params.value || 'N/A'} size="small" variant="outlined" />
      ),
    },
    {
      field: 'difficultyLevel',
      headerName: 'Difficulty',
      width: 120,
      renderCell: (params) => {
        const level = (params.value || '').toLowerCase();
        const color =
          level === 'advanced' ? 'error' : level === 'intermediate' ? 'warning' : 'success';
        return (
          <Chip
            label={params.value || 'N/A'}
            size="small"
            color={color as any}
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'language',
      headerName: 'Language',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2">{(params.value || '').toUpperCase()}</Typography>
      ),
    },
    {
      field: 'quizFocus',
      headerName: 'Focus',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value ? format(new Date(params.value), 'MMM dd, yyyy') : '-'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="View Details">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setQuizDetailDialog({ open: true, quiz: params.row });
            }}
          >
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Content Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => {
            fetchVideos();
            fetchQuizzes();
            fetchStats();
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}
      >
        <Tab
          icon={<VideoLibrary />}
          iconPosition="start"
          label={`Videos (${totalVideos})`}
        />
        <Tab
          icon={<QuizIcon />}
          iconPosition="start"
          label={`Quizzes (${totalQuizzes})`}
        />
      </Tabs>

      {/* ================================================================== */}
      {/* VIDEOS TAB                                                         */}
      {/* ================================================================== */}
      <TabPanel value={activeTab} index={0}>
        {/* Generation Monitor */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ pb: '16px !important' }}>
                <Typography variant="overline" color="text.secondary">
                  Total Videos
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {statsLoading ? <CircularProgress size={28} /> : stats.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ pb: '16px !important' }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <QuizIcon fontSize="small" color="primary" />
                  <Typography variant="overline" color="text.secondary">
                    With Quiz
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {statsLoading ? <CircularProgress size={28} /> : stats.withQuiz}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={pct(stats.withQuiz, stats.total)}
                  color="primary"
                  sx={{ mt: 1, height: 6, borderRadius: 3 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {pct(stats.withQuiz, stats.total)}% coverage
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ pb: '16px !important' }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Translate fontSize="small" color="secondary" />
                  <Typography variant="overline" color="text.secondary">
                    With Vocabulary
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {statsLoading ? <CircularProgress size={28} /> : stats.withVocabulary}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={pct(stats.withVocabulary, stats.total)}
                  color="secondary"
                  sx={{ mt: 1, height: 6, borderRadius: 3 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {pct(stats.withVocabulary, stats.total)}% coverage
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ pb: '16px !important' }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Subtitles fontSize="small" color="success" />
                  <Typography variant="overline" color="text.secondary">
                    With Subtitles
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {statsLoading ? <CircularProgress size={28} /> : stats.withSubtitles}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={pct(stats.withSubtitles, stats.total)}
                  color="success"
                  sx={{ mt: 1, height: 6, borderRadius: 3 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {pct(stats.withSubtitles, stats.total)}% coverage
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search videos by title..."
                  value={videoSearch}
                  onChange={(e) => {
                    setVideoSearch(e.target.value);
                    setVideosPage(0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Source Type</InputLabel>
                  <Select
                    value={sourceTypeFilter}
                    label="Source Type"
                    onChange={(e) => {
                      setSourceTypeFilter(e.target.value);
                      setVideosPage(0);
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="local">Local</MenuItem>
                    <MenuItem value="youtube_shorts">YouTube</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Published</InputLabel>
                  <Select
                    value={publishedFilter}
                    label="Published"
                    onChange={(e) => {
                      setPublishedFilter(e.target.value);
                      setVideosPage(0);
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="yes">Published</MenuItem>
                    <MenuItem value="no">Unpublished</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Generation</InputLabel>
                  <Select
                    value={generationFilter}
                    label="Generation"
                    onChange={(e) => {
                      setGenerationFilter(e.target.value);
                      setVideosPage(0);
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="missing_quiz">Missing Quiz</MenuItem>
                    <MenuItem value="missing_vocabulary">Missing Vocabulary</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="text"
                  size="small"
                  onClick={() => {
                    setVideoSearch('');
                    setSourceTypeFilter('');
                    setPublishedFilter('');
                    setGenerationFilter('');
                    setVideosPage(0);
                  }}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Videos Table */}
        <Card>
          <DataGrid
            rows={videos}
            columns={videoColumns}
            loading={videosLoading}
            paginationMode="server"
            rowCount={totalVideos}
            paginationModel={{ page: videosPage, pageSize: videosPageSize }}
            onPaginationModelChange={(model) => {
              setVideosPage(model.page);
              setVideosPageSize(model.pageSize);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            rowHeight={60}
            sx={{ ...defaultDataGridStyles, height: 'calc(100vh - 520px)', minHeight: 400 }}
            onRowClick={(params) =>
              setVideoDetailDialog({ open: true, video: params.row })
            }
          />
        </Card>
      </TabPanel>

      {/* ================================================================== */}
      {/* QUIZZES TAB                                                        */}
      {/* ================================================================== */}
      <TabPanel value={activeTab} index={1}>
        <Card>
          <DataGrid
            rows={quizzes}
            columns={quizColumns}
            loading={quizzesLoading}
            paginationMode="server"
            rowCount={totalQuizzes}
            paginationModel={{ page: quizzesPage, pageSize: quizzesPageSize }}
            onPaginationModelChange={(model) => {
              setQuizzesPage(model.page);
              setQuizzesPageSize(model.pageSize);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            sx={{ ...defaultDataGridStyles, height: 'calc(100vh - 320px)', minHeight: 400 }}
          />
        </Card>
      </TabPanel>

      {/* ================================================================== */}
      {/* VIDEO DETAIL DIALOG                                                */}
      {/* ================================================================== */}
      <Dialog
        open={videoDetailDialog.open}
        onClose={() => setVideoDetailDialog({ open: false, video: null })}
        maxWidth="md"
        fullWidth
      >
        {videoDetailDialog.video && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={1}>
                <VideoLibrary color="primary" />
                <Typography variant="h6" component="span">
                  Video Details
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                {/* Left: thumbnail + basic info */}
                <Grid item xs={12} md={5}>
                  {videoDetailDialog.video.thumbnailUrl ? (
                    <Box
                      component="img"
                      src={videoDetailDialog.video.thumbnailUrl}
                      alt={videoDetailDialog.video.title}
                      sx={{
                        width: '100%',
                        borderRadius: 2,
                        mb: 2,
                        objectFit: 'cover',
                        maxHeight: 220,
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: 180,
                        borderRadius: 2,
                        mb: 2,
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PlayArrow sx={{ fontSize: 48, color: 'grey.400' }} />
                    </Box>
                  )}

                  <Typography variant="h6" gutterBottom>
                    {videoDetailDialog.video.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {videoDetailDialog.video.description || 'No description'}
                  </Typography>

                  {videoDetailDialog.video.videoUrl && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<OpenInNew />}
                      href={videoDetailDialog.video.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ mb: 2 }}
                    >
                      Open Video URL
                    </Button>
                  )}
                </Grid>

                {/* Right: metadata */}
                <Grid item xs={12} md={7}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Video Information
                  </Typography>
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Grid container spacing={1.5}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Source Type
                          </Typography>
                          <Box>
                            <Chip
                              label={
                                videoDetailDialog.video.sourceType === 'youtube_shorts'
                                  ? 'YouTube Shorts'
                                  : 'Local Upload'
                              }
                              size="small"
                              color={
                                videoDetailDialog.video.sourceType === 'youtube_shorts'
                                  ? 'error'
                                  : 'info'
                              }
                              variant="outlined"
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Published
                          </Typography>
                          <Box>
                            <Chip
                              label={videoDetailDialog.video.isPublished ? 'Published' : 'Draft'}
                              size="small"
                              color={videoDetailDialog.video.isPublished ? 'success' : 'default'}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Duration
                          </Typography>
                          <Typography variant="body2">
                            {formatDuration(videoDetailDialog.video.durationSeconds)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Subject
                          </Typography>
                          <Typography variant="body2">
                            {videoDetailDialog.video.subject || '-'}
                          </Typography>
                        </Grid>
                        {videoDetailDialog.video.youtubeVideoId && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              YouTube Video ID
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {videoDetailDialog.video.youtubeVideoId}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>

                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Creator
                  </Typography>
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Grid container spacing={1.5}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Username
                          </Typography>
                          <Typography variant="body2">
                            {videoDetailDialog.video.creatorUsername || videoDetailDialog.video.creatorName || '-'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Email
                          </Typography>
                          <Typography variant="body2">
                            {videoDetailDialog.video.creatorEmail || '-'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Creator ID
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {videoDetailDialog.video.creatorId}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Engagement
                  </Typography>
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Grid container spacing={1.5}>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Views</Typography>
                          <Typography variant="h6">{videoDetailDialog.video.viewCount.toLocaleString()}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Likes</Typography>
                          <Typography variant="h6">{videoDetailDialog.video.likeCount.toLocaleString()}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Comments</Typography>
                          <Typography variant="h6">{videoDetailDialog.video.commentCount.toLocaleString()}</Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Generation Status
                  </Typography>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" gap={1.5} flexWrap="wrap">
                        <Chip
                          icon={<QuizIcon />}
                          label={videoDetailDialog.video.quizGenerated ? 'Quiz Generated' : 'No Quiz'}
                          color={videoDetailDialog.video.quizGenerated ? 'success' : 'default'}
                          variant={videoDetailDialog.video.quizGenerated ? 'filled' : 'outlined'}
                        />
                        <Chip
                          icon={<Translate />}
                          label={
                            videoDetailDialog.video.vocabularyGenerated
                              ? 'Vocabulary Generated'
                              : 'No Vocabulary'
                          }
                          color={videoDetailDialog.video.vocabularyGenerated ? 'success' : 'default'}
                          variant={videoDetailDialog.video.vocabularyGenerated ? 'filled' : 'outlined'}
                        />
                        <Chip
                          icon={<Subtitles />}
                          label={
                            videoDetailDialog.video.subtitlesGenerated
                              ? 'Subtitles Generated'
                              : 'No Subtitles'
                          }
                          color={videoDetailDialog.video.subtitlesGenerated ? 'success' : 'default'}
                          variant={videoDetailDialog.video.subtitlesGenerated ? 'filled' : 'outlined'}
                        />
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Quizzes */}
                  {loadingVideoDetails ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
                  ) : videoQuiz && videoQuiz.length > 0 ? (
                    <Accordion defaultExpanded sx={{ mt: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <QuizIcon color="primary" fontSize="small" />
                          <Typography variant="subtitle2" fontWeight="bold">Quizzes ({videoQuiz.length})</Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {videoQuiz.map((quiz: any, idx: number) => (
                          <Box key={quiz.quiz_id || idx} sx={{ mb: idx < videoQuiz.length - 1 ? 2 : 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="body2" fontWeight="bold">{quiz.title || `Quiz ${idx + 1}`}</Typography>
                              <Chip label={quiz.language || 'N/A'} size="small" variant="outlined" />
                              <Chip label={quiz.category || 'N/A'} size="small" variant="outlined" />
                              {quiz.difficulty_level && <Chip label={quiz.difficulty_level} size="small" color="primary" variant="outlined" />}
                              <Chip label={`${quiz.question_count || 0} questions`} size="small" />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                              <Typography variant="caption" color="text.secondary">Attempts: {quiz.total_attempts || 0}</Typography>
                              <Typography variant="caption" color="text.secondary">Avg Score: {quiz.average_score_percentage || 0}%</Typography>
                            </Box>
                            {quiz.questions && Array.isArray(quiz.questions) && quiz.questions.length > 0 ? (
                              editingQuizId === quiz.quiz_id ? (
                                <Box>
                                  <TextField
                                    multiline
                                    fullWidth
                                    minRows={6}
                                    maxRows={15}
                                    value={editingQuizJson}
                                    onChange={(e) => setEditingQuizJson(e.target.value)}
                                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem', mb: 1 }}
                                    InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.75rem' } }}
                                  />
                                  <Box display="flex" gap={1}>
                                    <Button size="small" variant="contained" startIcon={savingQuiz ? <CircularProgress size={14} color="inherit" /> : <Save />} onClick={() => handleSaveQuiz(quiz.quiz_id)} disabled={savingQuiz}>
                                      Save
                                    </Button>
                                    <Button size="small" variant="outlined" onClick={() => setEditingQuizId(null)} disabled={savingQuiz}>
                                      Cancel
                                    </Button>
                                  </Box>
                                </Box>
                              ) : (
                                <Box>
                                  <Box component="pre" sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: 1, fontSize: '0.75rem', maxHeight: 250, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {JSON.stringify(quiz.questions, null, 2)}
                                  </Box>
                                  <Button size="small" startIcon={<Edit />} sx={{ mt: 1 }} onClick={() => { setEditingQuizId(quiz.quiz_id); setEditingQuizJson(JSON.stringify(quiz.questions, null, 2)); }}>
                                    Edit Questions
                                  </Button>
                                </Box>
                              )
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No question data available</Typography>
                            )}
                            {idx < videoQuiz.length - 1 && <Divider sx={{ mt: 2 }} />}
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  ) : !loadingVideoDetails ? (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">No quizzes found for this video</Typography>
                    </Box>
                  ) : null}

                  {/* Add Grammar Question Button */}
                  {!loadingVideoDetails && videoDetailDialog.video && (
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<Spellcheck />}
                        onClick={() => openGrammarDialog(videoDetailDialog.video!.id)}
                      >
                        Add Grammar Question
                      </Button>
                    </Box>
                  )}

                  {/* Vocabulary Words */}
                  {!loadingVideoDetails && videoVocab.length === 0 && videoQuiz && (
                    <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">No vocabulary words found for this video</Typography>
                    </Box>
                  )}
                  {!loadingVideoDetails && videoVocab.length > 0 && (
                    <Accordion defaultExpanded sx={{ mt: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Translate color="secondary" fontSize="small" />
                          <Typography variant="subtitle2" fontWeight="bold">Vocabulary ({videoVocab.length} words)</Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Word</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Part of Speech</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>English Definition</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Chinese Definition</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Context (EN)</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Context (中文)</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: 80 }}>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {videoVocab.map((word: any) => (
                                editingVocabId === word.id ? (
                                  <TableRow key={word.id}>
                                    <TableCell>
                                      <TextField size="small" value={editingVocab.word || ''} onChange={e => setEditingVocab({ ...editingVocab, word: e.target.value })} fullWidth variant="standard" />
                                    </TableCell>
                                    <TableCell>
                                      <TextField size="small" value={editingVocab.part_of_speech || ''} onChange={e => setEditingVocab({ ...editingVocab, part_of_speech: e.target.value })} fullWidth variant="standard" />
                                    </TableCell>
                                    <TableCell>
                                      <TextField size="small" value={editingVocab.definition_en || ''} onChange={e => setEditingVocab({ ...editingVocab, definition_en: e.target.value })} fullWidth variant="standard" multiline />
                                    </TableCell>
                                    <TableCell>
                                      <TextField size="small" value={editingVocab.definition_zh || ''} onChange={e => setEditingVocab({ ...editingVocab, definition_zh: e.target.value })} fullWidth variant="standard" multiline />
                                    </TableCell>
                                    <TableCell>
                                      <TextField size="small" value={editingVocab.context_sentence || ''} onChange={e => setEditingVocab({ ...editingVocab, context_sentence: e.target.value })} fullWidth variant="standard" multiline />
                                    </TableCell>
                                    <TableCell>
                                      <TextField size="small" value={editingVocab.context_translation || ''} onChange={e => setEditingVocab({ ...editingVocab, context_translation: e.target.value })} fullWidth variant="standard" multiline />
                                    </TableCell>
                                    <TableCell>
                                      <Box display="flex" gap={0.5}>
                                        <IconButton size="small" color="primary" onClick={() => handleSaveVocab(word.id)} disabled={savingVocab}>
                                          {savingVocab ? <CircularProgress size={16} /> : <Save fontSize="small" />}
                                        </IconButton>
                                        <IconButton size="small" onClick={() => setEditingVocabId(null)} disabled={savingVocab}>
                                          <Cancel fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  <TableRow key={word.id} hover>
                                    <TableCell sx={{ fontWeight: 'medium' }}>{word.word || word.base_form || '-'}</TableCell>
                                    <TableCell><Chip label={word.part_of_speech || '-'} size="small" variant="outlined" /></TableCell>
                                    <TableCell sx={{ maxWidth: 180, fontSize: '0.8rem' }}>{word.definition_en || '-'}</TableCell>
                                    <TableCell sx={{ maxWidth: 180, fontSize: '0.8rem' }}>{word.definition_zh || '-'}</TableCell>
                                    <TableCell sx={{ maxWidth: 200, fontSize: '0.8rem' }}>{word.context_sentence || '-'}</TableCell>
                                    <TableCell sx={{ maxWidth: 200, fontSize: '0.8rem' }}>{word.context_translation || '-'}</TableCell>
                                    <TableCell>
                                      <IconButton size="small" onClick={() => { setEditingVocabId(word.id); setEditingVocab({ word: word.word, part_of_speech: word.part_of_speech, definition_en: word.definition_en, definition_zh: word.definition_zh, context_sentence: word.context_sentence, context_translation: word.context_translation }); }}>
                                        <Edit fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                )
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Transcript / Subtitles */}
                  {!loadingVideoDetails && videoSubtitles.length > 0 && (
                    <Accordion sx={{ mt: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Subtitles color="success" fontSize="small" />
                          <Typography variant="subtitle2" fontWeight="bold">Transcript ({videoSubtitles.length} segments)</Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', width: 60 }}>Time</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>English</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>中文翻譯</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: 80 }}>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {videoSubtitles.map((seg: any, idx: number) => {
                                const startSec = seg.start_time || seg.start_ms / 1000 || 0;
                                const mins = Math.floor(startSec / 60);
                                const secs = Math.floor(startSec % 60);
                                const timeStr = `${mins}:${secs.toString().padStart(2, '0')}`;
                                return editingSubId === seg.id ? (
                                  <TableRow key={seg.id || idx}>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'primary.main' }}>{timeStr}</TableCell>
                                    <TableCell>
                                      <TextField size="small" value={editingSub.text_en} onChange={e => setEditingSub({ ...editingSub, text_en: e.target.value })} fullWidth variant="standard" multiline />
                                    </TableCell>
                                    <TableCell>
                                      <TextField size="small" value={editingSub.text_zh} onChange={e => setEditingSub({ ...editingSub, text_zh: e.target.value })} fullWidth variant="standard" multiline />
                                    </TableCell>
                                    <TableCell>
                                      <Box display="flex" gap={0.5}>
                                        <IconButton size="small" color="primary" onClick={() => handleSaveSubtitle(seg.id)} disabled={savingSub}>
                                          {savingSub ? <CircularProgress size={16} /> : <Save fontSize="small" />}
                                        </IconButton>
                                        <IconButton size="small" onClick={() => setEditingSubId(null)} disabled={savingSub}>
                                          <Cancel fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  <TableRow key={seg.id || idx} hover>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'primary.main' }}>{timeStr}</TableCell>
                                    <TableCell sx={{ fontSize: '0.85rem' }}>{seg.text_en || '-'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.85rem' }}>{seg.text_zh || '-'}</TableCell>
                                    <TableCell>
                                      <IconButton size="small" onClick={() => { setEditingSubId(seg.id); setEditingSub({ text_en: seg.text_en || '', text_zh: seg.text_zh || '' }); }}>
                                        <Edit fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  )}
                  {!loadingVideoDetails && videoSubtitles.length === 0 && videoQuiz && (
                    <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">No transcript/subtitles found for this video</Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
              {videoDetailDialog.video.youtubeVideoId && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={regenerating ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                  disabled={regenerating}
                  onClick={() => handleRegenerateQuiz(videoDetailDialog.video!)}
                >
                  {regenerating ? 'Generating...' : 'Regenerate Quiz'}
                </Button>
              )}
              <Button
                variant={videoDetailDialog.video.isPublished ? 'outlined' : 'contained'}
                color={videoDetailDialog.video.isPublished ? 'warning' : 'success'}
                startIcon={videoDetailDialog.video.isPublished ? <VisibilityOff /> : <Visibility />}
                onClick={() => {
                  handleTogglePublish(videoDetailDialog.video!);
                  setVideoDetailDialog({ open: false, video: null });
                }}
              >
                {videoDetailDialog.video.isPublished ? 'Unpublish' : 'Publish'}
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button onClick={() => setVideoDetailDialog({ open: false, video: null })}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ================================================================== */}
      {/* QUIZ DETAIL DIALOG                                                 */}
      {/* ================================================================== */}
      <Dialog
        open={quizDetailDialog.open}
        onClose={() => setQuizDetailDialog({ open: false, quiz: null })}
        maxWidth="md"
        fullWidth
      >
        {quizDetailDialog.quiz && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={1}>
                <QuizIcon color="primary" />
                <Typography variant="h6" component="span">
                  Quiz Details
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Title</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {quizDetailDialog.quiz.title}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Video</Typography>
                  <Typography variant="body2">{quizDetailDialog.quiz.videoTitle}</Typography>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Typography variant="caption" color="text.secondary">Category</Typography>
                  <Box><Chip label={quizDetailDialog.quiz.category || 'N/A'} size="small" /></Box>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Typography variant="caption" color="text.secondary">Difficulty</Typography>
                  <Box><Chip label={quizDetailDialog.quiz.difficultyLevel || 'N/A'} size="small" variant="outlined" /></Box>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Typography variant="caption" color="text.secondary">Language</Typography>
                  <Typography variant="body2">{(quizDetailDialog.quiz.language || '').toUpperCase()}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 2 }} />

              {/* English Questions */}
              {quizDetailDialog.quiz.questionsEn && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    English Questions
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      bgcolor: 'grey.50',
                      p: 2,
                      borderRadius: 1,
                      fontSize: '0.8rem',
                      maxHeight: 300,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {typeof quizDetailDialog.quiz.questionsEn === 'string'
                      ? quizDetailDialog.quiz.questionsEn
                      : JSON.stringify(quizDetailDialog.quiz.questionsEn, null, 2)}
                  </Box>
                </Box>
              )}

              {/* Chinese Questions */}
              {quizDetailDialog.quiz.questionsZh && (
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Chinese Questions
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      bgcolor: 'grey.50',
                      p: 2,
                      borderRadius: 1,
                      fontSize: '0.8rem',
                      maxHeight: 300,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {typeof quizDetailDialog.quiz.questionsZh === 'string'
                      ? quizDetailDialog.quiz.questionsZh
                      : JSON.stringify(quizDetailDialog.quiz.questionsZh, null, 2)}
                  </Box>
                </Box>
              )}

              {!quizDetailDialog.quiz.questionsEn && !quizDetailDialog.quiz.questionsZh && (
                <Typography variant="body2" color="text.secondary">
                  No question data available for this quiz.
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setQuizDetailDialog({ open: false, quiz: null })}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ================================================================== */}
      {/* DELETE CONFIRM DIALOG                                              */}
      {/* ================================================================== */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, video: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{deleteConfirmDialog.video?.title}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialog({ open: false, video: null })}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteVideo}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================================================== */}
      {/* GRAMMAR QUESTION DIALOG                                            */}
      {/* ================================================================== */}
      <Dialog
        open={grammarDialogOpen}
        onClose={() => setGrammarDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: 500 } }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Spellcheck color="secondary" />
            <Typography variant="h6" component="span">Add Grammar Question</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <ToggleButtonGroup
            value={grammarMode}
            exclusive
            onChange={(_, v) => { if (v) { setGrammarMode(v); setGrammarErrors([]); } }}
            size="small"
            sx={{ mb: 2 }}
          >
            <ToggleButton value="form"><Edit sx={{ mr: 0.5 }} fontSize="small" />Form</ToggleButton>
            <ToggleButton value="json"><ContentPaste sx={{ mr: 0.5 }} fontSize="small" />Paste JSON</ToggleButton>
          </ToggleButtonGroup>

          {grammarErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {grammarErrors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </Alert>
          )}

          {grammarMode === 'form' ? (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Grammar Skill *</InputLabel>
                    <Select
                      value={grammarForm.grammar_skill}
                      label="Grammar Skill *"
                      onChange={e => setGrammarForm({ ...grammarForm, grammar_skill: e.target.value })}
                    >
                      {GRAMMAR_SKILLS.map(s => (
                        <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Source Sentence (original from transcript) *"
                    value={grammarForm.source_sentence}
                    onChange={e => setGrammarForm({ ...grammarForm, source_sentence: e.target.value })}
                    helperText="The original unmodified sentence. Min 8 words."
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Sentence with Blanks *"
                    value={grammarForm.sentence_with_blanks}
                    onChange={e => {
                      const val = e.target.value;
                      const count = (val.match(/___/g) || []).length;
                      const answers = grammarForm.correct_answers.length === count
                        ? grammarForm.correct_answers
                        : Array(count).fill('');
                      setGrammarForm({ ...grammarForm, sentence_with_blanks: val, correct_answers: answers });
                    }}
                    helperText={`Replace target word(s) with ___. Detected ${blankCount} blank(s).`}
                  />
                </Grid>

                {blankCount > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Correct Answers (one per blank) *
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {grammarForm.correct_answers.map((ans, idx) => (
                        <TextField
                          key={idx}
                          size="small"
                          label={`Blank ${idx + 1}`}
                          value={ans}
                          onChange={e => {
                            const updated = [...grammarForm.correct_answers];
                            updated[idx] = e.target.value;
                            setGrammarForm({ ...grammarForm, correct_answers: updated });
                          }}
                          sx={{ width: 150 }}
                        />
                      ))}
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Word Bank (comma-separated) *"
                    value={grammarForm.word_bank}
                    onChange={e => setGrammarForm({ ...grammarForm, word_bank: e.target.value })}
                    helperText="Include all correct answers + 2-4 distractors. E.g.: with, to, for, by"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Explanation (English) *"
                    value={grammarForm.explanation_en}
                    onChange={e => setGrammarForm({ ...grammarForm, explanation_en: e.target.value })}
                    multiline
                    minRows={2}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Explanation (中文) *"
                    value={grammarForm.explanation_zh}
                    onChange={e => setGrammarForm({ ...grammarForm, explanation_zh: e.target.value })}
                    multiline
                    minRows={2}
                    helperText='Use 「」for English words in Chinese text'
                  />
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Paste a single grammar question JSON object or an array of objects.
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={12}
                maxRows={20}
                value={grammarJson}
                onChange={e => setGrammarJson(e.target.value)}
                placeholder={`{\n  "grammar_skill": "prepositions",\n  "source_sentence": "...",\n  "sentence_with_blanks": "He had to cope ___ the consequences.",\n  "blanks": [{ "position": 0, "correct_answer": "with" }],\n  "word_bank": ["with", "to", "for", "by"],\n  "explanation_en": "...",\n  "explanation_zh": "..."\n}`}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setGrammarDialogOpen(false)} disabled={savingGrammar}>Cancel</Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={savingGrammar ? <CircularProgress size={16} color="inherit" /> : <Add />}
            onClick={handleSaveGrammarQuestion}
            disabled={savingGrammar}
          >
            {savingGrammar ? 'Saving...' : 'Add Grammar Question'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContentManagementPage;
